/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import { Alert, AlertTitle, Button, Typography } from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import { useAtomValue, useSetAtom } from 'jotai';
import React from 'react';
import { incidentConfigAtom } from './api';
import { loadable } from '../utils/loadable';

/**
 * Why the incident page list could not be served, with a retry. Rendered
 * wherever the list's absence would otherwise pass unnoticed: the dashboard,
 * whose lists would simply be empty, and the incident sidebar, whose page
 * would show every POST run the config was meant to hide. Nothing while the
 * request is in flight or once it has succeeded.
 */
export const IncidentConfigAlert = React.memo(({ title }: { title: string }) => {
  const config = useAtomValue(loadable(incidentConfigAtom));
  const refresh = useSetAtom(incidentConfigAtom);

  if (config.state !== 'hasError') {
    return null;
  }
  return (
    <Alert
      severity="error"
      action={
        <Button color="inherit" size="small" startIcon={<ReplayIcon />} onClick={() => refresh()}>
          Retry
        </Button>
      }
    >
      <AlertTitle>{title}</AlertTitle>
      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
        {config.error instanceof Error ? config.error.message : String(config.error)}
      </Typography>
    </Alert>
  );
});
